import { IFriendData, IPlayerState } from '../../../State/Player/IPlayerState';
import IPlayerData from '../../IPlayerData';
import { restoreStringifiedArray } from '../../ParseHelper';
import PlayerProperty from '../PlayerProperty';

type GiftTimer = {
    friendID: string;
    date: string;
};

type HelperTimer = {
    friendID: string;
    date: string;
};

export default class Friends extends PlayerProperty<
    IFriendData[],
    {
        request_mas: string;
        send_mas: string;
        gift_timers: string;
        helper_timers: string;
    }
> {
    public readonly defaultValue: IFriendData[] = [];

    public restore(playerEndpointData: IPlayerData): IFriendData[] {
        const request_mas = restoreStringifiedArray(
            playerEndpointData.request_mas,
            []
        );
        const send_mas = restoreStringifiedArray(
            playerEndpointData.send_mas,
            []
        );
        const gift_timers = restoreStringifiedArray<GiftTimer>(
            playerEndpointData.gift_timers,
            []
        );

        const helper_timers = restoreStringifiedArray<HelperTimer>(
            playerEndpointData.helper_timers,
            []
        );

        const friendData: IFriendData[] = [];
        for (let i = 0, n = request_mas.length; i < n; i++) {
            const split = request_mas[i].split('%');
            const friendPlayerId = split[0];
            const lifeRequestDateString = split[1];
            const friend = this.getFriend(friendPlayerId, friendData);
            friend.lifeRequestTimer = lifeRequestDateString;
        }
        for (let i = 0, n = send_mas.length; i < n; i++) {
            const split = send_mas[i].split('%');
            const friendPlayerId = split[0];
            const lifeSendDateString = split[1];
            const friend = this.getFriend(friendPlayerId, friendData);
            friend.lifeSendTimer = lifeSendDateString;
        }
        for (let i = 0, n = gift_timers.length; i < n; i++) {
            const friendPlayerId = gift_timers[i].friendID;
            const friend = this.getFriend(friendPlayerId, friendData);
            friend.giftTimer = gift_timers[i].date;
        }
        for (let i = 0, n = helper_timers.length; i < n; i++) {
            const friendPlayerId = helper_timers[i].friendID;
            const friend = this.getFriend(friendPlayerId, friendData);
            friend.helperTimer = helper_timers[i].date;
        }

        return friendData;
    }

    public save(playerStateData: IPlayerState) {
        return {
            request_mas: JSON.stringify(
                this.getFriendRequestArray(playerStateData.friendData)
            ),
            send_mas: JSON.stringify(
                this.getFriendSendArray(playerStateData.friendData)
            ),
            gift_timers: JSON.stringify(
                this.getGiftArray(playerStateData.friendData)
            ),
            helper_timers: JSON.stringify(
                this.getHelperArray(playerStateData.friendData)
            )
        };
    }

    public isChanged(
        playerStateData: IPlayerState,
        playerEndpointData: IPlayerData
    ): boolean {
        const saveData = this.save(playerStateData);

        return (
            playerEndpointData.send_mas !== saveData.send_mas ||
            playerEndpointData.request_mas !== saveData.request_mas ||
            playerEndpointData.gift_timers !== saveData.gift_timers ||
            playerEndpointData.helper_timers !== saveData.helper_timers
        );
    }

    private getGiftArray(friendData: IFriendData[]): GiftTimer[] {
        const gift_timer: GiftTimer[] = [];
        friendData.forEach(value => {
            if (value.giftTimer) {
                gift_timer.push({
                    friendID: value.playerId,
                    date: value.giftTimer,
                });
            }
        });

        return gift_timer;
    }

    private getHelperArray(friendData: IFriendData[]): HelperTimer[] {
        const helper_timer: HelperTimer[] = [];
        friendData.forEach(value => {
            if (value.helperTimer) {
                helper_timer.push({
                    friendID: value.playerId,
                    date: value.helperTimer,
                });
            }
        });

        return helper_timer;
    }

    private getFriendRequestArray(friendData: IFriendData[]): string[] {
        const request_mas: string[] = [];
        friendData.forEach(value => {
            if (value.lifeRequestTimer) {
                request_mas.push(`${value.playerId}%${value.lifeRequestTimer}`);
            }
        });

        return request_mas;
    }

    private getFriendSendArray(friendData: IFriendData[]): string[] {
        const send_mas: string[] = [];
        friendData.forEach(value => {
            if (value.lifeSendTimer) {
                send_mas.push(`${value.playerId}%${value.lifeSendTimer}`);
            }
        });

        return send_mas;
    }

    private getFriend(
        friendPlayerId: string,
        friendData: IFriendData[]
    ): IFriendData {
        // Look for friend data already stored.
        let friend: IFriendData = friendData.find(
            friend => friend.playerId === friendPlayerId
        );

        // If friend not found add a new entry.
        if (!friend) {
            // Friend properties must be initialized with nulls for null-checking elsewhere.
            friend = {
                playerId: friendPlayerId,
                lifeSendTimer: null,
                lifeRequestTimer: null,
                giftTimer: null,
                helperTimer: null
            };
            friendData.push(friend);
        }

        return friend;
    }
}
